<template>
    <div class="bg-white flex-1 relative">
        <loading :status="loading.form"/>
        <div class="flex relative">
            <div :style="{height: 'calc(100vh - 70px)'}" class="flex-auto min-w-0">
                <div class="w-full h:auto sm:h-14 border-b sm:px-3">
                    <div class="sm:flex sm:justify-between">
                        <div class="flex">
                            <button class="btn p-4 rounded-none" type="button" @click="$router.push('/dashboard/tickets')">
                                <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.chevron-left-regular"></svg-vue>
                            </button>
                            <div v-on-clickaway="closeActionDropdown" class="block">
                                <div class="relative inline-block text-left">
                                    <button class="btn p-4 rounded-none" type="button" @click="toggleActionDropdown('label')">
                                        <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.tags-regular"></svg-vue>
                                    </button>
                                    <div v-show="actions.label" class="origin-top-right absolute left-0 mt-1 w-56 rounded-md shadow-lg z-20">
                                        <div class="rounded-md bg-white shadow-xs">
                                            <div class="py-1">
                                                <div v-if="!ticket.labels || ticket.labels.length === 0" class="px-4 py-2 text-sm text-gray-400">
                                                    {{ $t('No labels') }}
                                                </div>
                                                <template v-for="label in labelList">
                                                    <a
                                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                        href="#"
                                                        role="menuitem"
                                                        @click.prevent="action('label', label.id)"
                                                    >
                                                        {{ label.name }}
                                                    </a>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="relative inline-block text-left">
                                    <button class="btn p-4 rounded-none" type="button" @click="toggleActionDropdown('priority')">
                                        <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.pennant-regular"></svg-vue>
                                    </button>
                                    <div v-show="actions.priority" class="origin-top-right absolute left-0 mt-1 w-56 rounded-md shadow-lg z-20">
                                        <div class="rounded-md bg-white shadow-xs">
                                            <div class="py-1">
                                                <template v-for="priority in priorityList">
                                                    <a
                                                        :class="ticket.priority && ticket.priority.id === priority.id ? 'block px-4 py-2 text-sm text-gray-700 font-semibold bg-gray-50 hover:bg-gray-100 hover:text-gray-900' : 'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900'"
                                                        href="#"
                                                        role="menuitem"
                                                        @click.prevent="action('priority', priority.id)"
                                                    >
                                                        {{ $t(priority.name) }}
                                                        <span v-if="ticket.priority && ticket.priority.id === priority.id" class="text-green-500 ml-1">&#10003;</span>
                                                    </a>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button v-if="canDelete" class="btn p-4 rounded-none" type="button" @click="deleteTicketModal = true">
                                <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.trash-alt-regular"></svg-vue>
                            </button>
                        </div>
                        <div class="flex items-center justify-end m-3 sm:m-0">
                            <div class="text-2xl font-semibold">#{{ ticket.id }}</div>
                            <div v-if="ticket.status" class="px-3">
                                <div class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
                                    {{ $t(ticket.status.name) }}
                                </div>
                            </div>
                            <div v-if="ticket.priority" class="pr-3">
                                <div class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800">
                                    {{ $t(ticket.priority.name) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ticket-conversation">
                    <div class="block">
                        <div class="sm:flex sm:items-center py-3 max-w-full border-b">
                            <div class="px-6 sm:pl-6 sm:pr-3 sm:flex-1 sm:w-3/4">
                                <div class="text-xl truncate">{{ ticket.subject }}</div>
                                <template v-for="(label, index) in ticket.labels">
                                    <div
                                        :style="{backgroundColor: label.color}"
                                        class="inline-flex items-center px-2 py-0.5 mr-1 rounded text-xs font-medium leading-4 text-gray-100"
                                    >
                                        {{ label.name }}
                                        <button class="flex-shrink-0 ml-1.5 inline-flex text-gray-100 focus:outline-none focus:text-gray-100 cursor-pointer" type="button" @click="removeLabel(index)">
                                            <svg-vue class="h-3 w-3" icon="font-awesome.times-solid"></svg-vue>
                                        </button>
                                    </div>
                                </template>
                            </div>
                            <div class="px-6 sm:pl-3 sm:pr-6 sm:flex-1 sm:w-1/4">
                                <div class="flex items-center sm:float-right mt-3 sm:mt-0">
                                    <div class="text-sm sm:pr-2">{{ ticket.created_at | momentFormatDateTimeAgo }}</div>
                                    <button class="flex items-center btn btn-white p-2 ml-3 sm:ml-0" type="button" @click="scrollToBottom">
                                        <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.reply-regular"></svg-vue>
                                        {{ $t('Reply') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <template v-if="ticket.ticketReplies.length > 0">
                            <div class="block">
                                <template v-for="(ticketReply, index) in ticket.ticketReplies">
                                    <div :class="{'border-t' : index > 0}" class="flex p-6">
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="ticketReply.user.avatar !== 'gravatar' ? ticketReply.user.avatar : ticketReply.user.gravatar"
                                            class="h-12 w-12 hidden sm:inline"
                                        />
                                        <div class="sm:pl-6 pb-2 w-full">
                                            <div class="md:flex md:items-center pb-1">
                                                <div class="md:flex-1 text-lg font-semibold text-gray-800">
                                                    {{ ticketReply.user.name }}
                                                </div>
                                                <div class="md:flex-1">
                                                    <div class="md:float-right text-sm">
                                                        {{ ticketReply.created_at | momentFormatDateTime }}
                                                    </div>
                                                </div>
                                            </div>
                                            <p class="text-gray-700 ticket-reply-body" v-html="ticketReply.body"/>
                                            <template v-if="ticketReply.attachments.length > 0">
                                                <div class="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                                    <template v-for="attachment in ticketReply.attachments">
                                                        <attachment :details="attachment" :remove-button="false"/>
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div class="h-full flex border-t">
                                <div class="m-auto">
                                    <div class="grid grid-cols-1 justify-items-center h-full w-full py-24">
                                        <div class="flex justify-center items-center">
                                            <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                                        </div>
                                        <div class="flex justify-center items-center">
                                            <div class="w-full font-semibold text-2xl">{{ $t('This ticket has no conversations') }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div v-show="replyForm" class="px-6 py-3 border-t" ref="replyFormBlock">
                            <loading :status="loading.reply"/>
                            <form @submit.prevent="addReply">
                                <input-wysiwyg
                                    ref="replyEditor"
                                    id="ticket_body"
                                    v-model="ticketReply.body"
                                    :cannedReplyList="cannedReplyList"
                                    :plugins="{images: true, cannedReply: true, attachment: true, shortCode: true}"
                                    @selectUploadFile="selectUploadFile"
                                >
                                    <template v-slot:top>
                                        <div v-for="item in uploadQueue" :key="item.id" class="flex items-center gap-2 mb-2">
                                            <span class="text-xs text-gray-600 truncate" style="max-width:150px" :title="item.fileName">{{ item.fileName }}</span>
                                            <span v-if="item.totalBytes" class="text-xs text-gray-400 whitespace-nowrap">
                                                {{ formatSize(item.uploadedBytes || 0) }} / {{ formatSize(item.totalBytes) }}
                                            </span>
                                            <div class="h-1 flex-1 bg-gray-200">
                                                <div :style="{width: item.percent + '%'}" class="bg-blue-500 py-0.5 transition-all"></div>
                                            </div>
                                            <span class="text-xs text-gray-500 whitespace-nowrap">
                                                <template v-if="item.processing">{{ $t('Processing file...') }}</template>
                                                <template v-else-if="item.speed > 0">
                                                    {{ item.speed }} MB/s
                                                    <template v-if="item.remaining > 0"> — ~{{ item.remaining }}s</template>
                                                </template>
                                                <template v-else>{{ $t('Preparing...') }}</template>
                                            </span>
                                            <button v-if="!item.processing" type="button" class="text-red-500 hover:text-red-700 text-sm font-bold px-1" @click="cancelUpload(item.id)">×</button>
                                        </div>
                                    </template>
                                    <template v-slot:bottom>
                                        <div class="flex justify-between border border-t-0">
                                            <button
                                                class="btn btn-secondary rounded-none"
                                                type="button"
                                                @click="discardReply"
                                            >
                                                {{ $t('Discard') }}
                                            </button>
                                            <div class="flex">
                                                <select
                                                    id="status"
                                                    v-model="ticketReply.status_id"
                                                    aria-label="Sort by"
                                                    class="block form-select pl-3 pr-9 py-2 border-l border-r-0 border-t-0 border-b-0 border-gray-400 rounded-none bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                                >
                                                    <template v-for="status in statusList">
                                                        <option :value="status.id">{{ $t(status.name) }}</option>
                                                    </template>
                                                </select>
                                                <button
                                                    class="btn btn-green rounded-none"
                                                    type="submit"
                                                >
                                                    {{ $t('Send reply') }}
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </input-wysiwyg>
                            </form>
                            <input ref="fileInput" hidden type="file" multiple @change="uploadFile($event)">
                            <template v-if="ticketReply.attachments.length > 0">
                                <div class="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                    <template v-for="(attachment, index) in ticketReply.attachments">
                                        <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                    </template>
                                </div>
                            </template>
                        </div>
                        <div ref="bottomReplyBtn" class="flex justify-end py-4 border-t pr-4">
                            <button class="flex items-center btn btn-white p-2" type="button" @click="scrollToBottom">
                                <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.reply-regular"></svg-vue>
                                {{ $t('Reply') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div :style="{flex: '0 0 280px', width: '280px'}" class="bg-gray-100 hidden lg:block border-l overflow-y-auto" style="max-height: calc(100vh - 70px);">
                <!-- User info -->
                <div v-if="ticket.user" class="p-6 border-b">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('Requester') }}</div>
                    <div class="flex">
                        <img
                            :alt="$t('Avatar')"
                            :src="ticket.user.avatar !== 'gravatar' ? ticket.user.avatar : ticket.user.gravatar"
                            class="h-12 w-12"
                        />
                        <div class="ml-3">
                            <div class="text-gray-800 font-medium truncate text-sm">{{ ticket.user.name }}</div>
                            <div class="flex items-center text-xs leading-5 text-gray-600">
                                <span class="truncate">{{ ticket.user.email }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Product info -->
                <div v-if="ticket.product" class="p-6 border-b">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('Product') }}</div>
                    <div class="text-sm text-gray-800">{{ ticket.product.name }}</div>
                    <div class="text-xs text-gray-500">S/N: {{ ticket.product.serial_number }}</div>
                    <div v-if="ticket.product.support_expired" class="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        {{ $t('Support expired') }}
                    </div>
                </div>
                <!-- Frontend info -->
                <div v-if="ticket.frontend" class="p-6 border-b">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('Frontend') }}</div>
                    <div class="text-sm text-gray-800">{{ ticket.frontend.name }}</div>
                </div>
                <!-- Involved Users -->
                <div class="p-6 border-b">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('Involved users') }}</div>
                    <template v-if="ticket.involved_users && ticket.involved_users.length > 0">
                        <div v-for="involvedUser in ticket.involved_users" class="flex items-center py-1">
                            <img
                                :alt="$t('Avatar')"
                                :src="involvedUser.avatar !== 'gravatar' ? involvedUser.avatar : involvedUser.gravatar"
                                class="h-6 w-6 rounded-full"
                            />
                            <span class="ml-2 text-sm text-gray-700 truncate flex-1">{{ involvedUser.name }}</span>
                            <button class="text-gray-400 hover:text-red-500 ml-1" type="button" @click="removeInvolvedUser(involvedUser.id)">
                                <svg-vue class="h-3 w-3" icon="font-awesome.times-solid"></svg-vue>
                            </button>
                        </div>
                    </template>
                    <div v-else class="text-xs text-gray-400">{{ $t('No involved users') }}</div>
                    <!-- Add user dropdown -->
                    <div class="mt-2">
                        <select
                            v-model="addUserId"
                            class="form-select block w-full text-xs py-1"
                            @change="addInvolvedUser"
                        >
                            <option :value="null">{{ $t('Add user...') }}</option>
                            <optgroup :label="$t('Employees')">
                                <template v-for="agent in agentList">
                                    <option :value="agent.id">{{ agent.name }}</option>
                                </template>
                            </optgroup>
                            <optgroup v-if="partnerList.length > 0" :label="$t('Partners')">
                                <template v-for="partner in partnerList">
                                    <option :value="partner.id">{{ partner.name }}</option>
                                </template>
                            </optgroup>
                            <optgroup v-if="clientList.length > 0" :label="$t('Clients')">
                                <template v-for="client in clientList">
                                    <option :value="client.id">{{ client.name }}</option>
                                </template>
                            </optgroup>
                        </select>
                    </div>
                </div>
                <!-- Escalation status (read-only for dashboard) -->
                <div v-if="ticket.frontend && ticket.is_escalated" class="p-6 border-b">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('Escalation') }}</div>
                    <div class="text-xs text-green-600">{{ $t('Ticket is escalated') }}</div>
                </div>
                <!-- SLA info -->
                <div v-if="ticket.time_started" class="p-6">
                    <div class="text-xs font-semibold text-gray-500 uppercase mb-2">{{ $t('SLA') }}</div>
                    <div class="text-xs text-gray-600">{{ $t('Time started') }}: {{ ticket.time_started | momentFormatDateTime }}</div>
                    <div v-if="ticket.last_updated" class="text-xs text-gray-600 mt-1">{{ $t('Last updated') }}: {{ ticket.last_updated | momentFormatDateTime }}</div>
                </div>
            </div>
        </div>
        <div v-show="deleteTicketModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="deleteTicketModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteTicketModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete ticket') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the ticket?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('All related data will be deleted') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteTicket"
                            >
                                {{ $t('Delete ticket') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteTicketModal = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import chunkedUpload from '../../../../utils/chunkedUpload';

const ROLE_ADMIN = 1;

export default {
    name: "manage",
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.ticket.id
                ? `#${this.ticket.id} - ${this.ticket.subject}`
                : this.$i18n.t('Manage ticket')
        }
    },
    computed: {
        isAdmin() {
            return this.$store.state.user && this.$store.state.user.role_id === ROLE_ADMIN;
        },
        canDelete() {
            return this.isAdmin || (this.$store.state.user && this.ticket.agent_id === this.$store.state.user.id);
        },
    },
    mounted() {
        this.getTicket();
        this.getFilters();
        this.getCannedReplies();
    },
    data() {
        return {
            loading: {
                form: true,
                reply: false,
            },
            deleteTicketModal: false,
            replyForm: false,
            uploadQueue: [],
            ticket: {
                subject: null,
                created_at: null,
                labels: [],
                ticketReplies: [],
            },
            ticketReply: {
                status_id: null,
                body: '',
                attachments: [],
            },
            actions: {
                agent: false,
                department: false,
                label: false,
                priority: false,
            },
            addUserId: null,
            cannedReplyList: [],
            agentList: [],
            partnerList: [],
            clientList: [],
            departmentList: [],
            labelList: [],
            statusList: [],
            priorityList: [],
        }
    },
    filters: {
        momentFormatDateTime: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' HH:mm');
        },
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        getTicket() {
            const self = this;
            self.loading.form = true;
            axios.get('api/dashboard/tickets/' + self.$route.params.uuid).then(function (response) {
                self.loading.form = false;
                self.ticket = response.data;
                self.ticketReply.status_id = response.data.status_id;
                self.getAvailableUsers();
            }).catch(function () {
                self.$router.push('/dashboard/tickets');
            });
        },
        getCannedReplies() {
            const self = this;
            axios.get('api/dashboard/tickets/canned-replies').then(function (response) {
                self.cannedReplyList = response.data;
            })
        },
        getFilters() {
            const self = this;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.departmentList = response.data.departments;
                self.labelList = response.data.labels;
                self.statusList = response.data.statuses;
                self.priorityList = response.data.priorities;
            });
        },
        getAvailableUsers() {
            const self = this;
            axios.get('api/dashboard/tickets/' + self.$route.params.uuid + '/available-users').then(function (response) {
                self.agentList = response.data.employees;
                self.partnerList = response.data.partners;
                self.clientList = response.data.clients || [];
            });
        },
        discardReply() {
            this.uploadQueue.forEach(function (entry) {
                if (entry.cancelSource) entry.cancelSource.cancel('Discard');
            });
            this.uploadQueue = [];
            this.ticketReply.body = '';
            this.ticketReply.attachments = [];
            this.replyForm = false;
        },
        selectUploadFile() {
            this.$refs.fileInput.click();
        },
        uploadFile(e) {
            var self = this;
            var files = Array.prototype.slice.call(e.target.files);
            if (!files.length) return;
            if (self.$refs.fileInput) self.$refs.fileInput.value = null;

            files.forEach(function (file) {
                var entry = {
                    id: Date.now() + '_' + Math.random(),
                    fileName: file.name,
                    percent: 0, speed: 0, remaining: 0,
                    processing: false, active: true,
                    uploadedBytes: 0, totalBytes: file.size,
                    cancelSource: axios.CancelToken.source(),
                };
                self.uploadQueue.push(entry);

                chunkedUpload(file, function (progress) {
                    entry.percent = progress.percent;
                    entry.speed = progress.speed;
                    entry.remaining = progress.remaining;
                    entry.processing = progress.processing;
                    entry.active = progress.active;
                    entry.uploadedBytes = progress.uploadedBytes || 0;
                    entry.totalBytes = progress.totalBytes || 0;
                    self.$forceUpdate();
                }, 'api/dashboard/tickets', entry.cancelSource.token).then(function (fileData) {
                    self.ticketReply.attachments.push(fileData);
                }).catch(function () {}).then(function () {
                    var idx = self.uploadQueue.findIndex(function (q) { return q.id === entry.id; });
                    if (idx !== -1) self.uploadQueue.splice(idx, 1);
                });
            });
        },
        cancelUpload(id) {
            var entry = this.uploadQueue.find(function (q) { return q.id === id; });
            if (entry && entry.cancelSource) {
                entry.cancelSource.cancel('Upload cancelled by user.');
            }
        },
        removeAttachment(index) {
            var attachment = this.ticketReply.attachments[index];
            if (attachment && attachment.uuid) {
                axios.delete('api/files/' + attachment.uuid).catch(function() {});
            }
            this.ticketReply.attachments.splice(index, 1);
        },
        removeLabel(index) {
            const self = this;
            const label = self.ticket.labels[index];
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/remove-label', {
                label: label.id,
            }).then(function () {
                self.ticket.labels.splice(index, 1);
            });
        },
        addReply() {
            const self = this;
            self.loading.reply = true;
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/reply', self.ticketReply).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.discardReply();
                self.loading.reply = false;
            }).catch(function () {
                self.loading.reply = false;
            });
        },
        openReplyForm() {
            this.replyForm = true;
            this.$nextTick(() => {
                const editor = this.$refs.replyEditor &&
                    this.$refs.replyEditor.$refs.editor &&
                    this.$refs.replyEditor.$refs.editor.editor;
                if (editor) editor.focus();
            });
        },
        scrollToBottom() {
            this.openReplyForm();
            setTimeout(() => {
                const el = this.$refs.bottomReplyBtn;
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 300);
        },
        closeActionDropdown() {
            this.actions.agent = false;
            this.actions.department = false;
            this.actions.label = false;
            this.actions.priority = false;
        },
        toggleActionDropdown(action) {
            if (action === 'agent') {
                this.actions.agent = !this.actions.agent;
                this.actions.department = false;
                this.actions.label = false;
                this.actions.priority = false;
            }
            if (action === 'department') {
                this.actions.department = !this.actions.department;
                this.actions.agent = false;
                this.actions.label = false;
                this.actions.priority = false;
            }
            if (action === 'label') {
                this.actions.label = !this.actions.label;
                this.actions.agent = false;
                this.actions.department = false;
                this.actions.priority = false;
            }
            if (action === 'priority') {
                this.actions.priority = !this.actions.priority;
                this.actions.agent = false;
                this.actions.department = false;
                this.actions.label = false;
            }
        },
        action(param, value) {
            const self = this;
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/quick-actions', {
                action: param,
                value: value,
            }).then(function (response) {
                self.closeActionDropdown();
                if (!response.data.access) {
                    self.$router.push('/dashboard/tickets');
                } else {
                    self.ticket = response.data.ticket;
                    self.ticketReply.status_id = response.data.ticket.status_id;
                }
            }).catch(function () {
                self.closeActionDropdown();
            });
        },
        deleteTicket() {
            const self = this;
            axios.delete('api/dashboard/tickets/' + self.$route.params.uuid).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/tickets');
            }).catch(function () {
                self.deleteTicketModal = false;
            });
        },
        escalateTicket() {
            const self = this;
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/escalate').then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Ticket has been escalated').toString(),
                    type: 'success'
                });
                self.ticket = response.data.ticket;
            }).catch(function () {});
        },
        addInvolvedUser() {
            const self = this;
            if (!self.addUserId) return;
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/add-involved-user', {
                user_id: self.addUserId,
            }).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('User has been added to the ticket').toString(),
                    type: 'success'
                });
                self.ticket = response.data.ticket;
                self.addUserId = null;
                self.getAvailableUsers();
            }).catch(function () {
                self.addUserId = null;
            });
        },
        formatSize(bytes) {
            if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
            if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
            return (bytes / 1024).toFixed(0) + ' KB';
        },
        removeInvolvedUser(userId) {
            const self = this;
            axios.post('api/dashboard/tickets/' + self.$route.params.uuid + '/remove-involved-user', {
                user_id: userId,
            }).then(function (response) {
                self.ticket = response.data.ticket;
                self.getAvailableUsers();
            }).catch(function () {});
        }
    }
}
</script>
